import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import './Chat.css';

export const UserChat: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchMessages = async () => {
    try {
      const res = await apiService.getConversation('admin');
      setMessages(res.data || []);
      // Mark as read
      await apiService.markMessagesAsRead('admin');
    } catch (err) {
      console.error('Failed to load chat history', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000); // Poll every 5s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      const sentMsg = await apiService.sendMessage(newMessage, ''); // Empty receiver defaults to admin
      setMessages(prev => [...prev, sentMsg.data]);
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message', err);
      alert(language === 'vi' ? 'Không thể gửi tin nhắn' : 'Failed to send message');
    }
  };

  return (
    <div className="chat-page">
      <div className="chat-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h2>{language === 'vi' ? 'Chat với Admin' : 'Chat with Admin'}</h2>
      </div>

      <div className="chat-window">
        {loading ? (
          <div className="chat-loading">Loading...</div>
        ) : (
          <div className="message-list">
            {messages.length === 0 && (
              <div className="no-messages">
                {language === 'vi' 
                  ? 'Hãy bắt đầu cuộc hội thoại! Quản trị viên sẽ sớm trả lời bạn.' 
                  : 'Start the conversation! The system admin will reply shortly.'}
              </div>
            )}
            {messages.map((msg) => {
              const isMine = msg.senderId === user?._id;
              return (
                <div key={msg._id || msg.id} className={`message-bubble ${isMine ? 'mine' : 'theirs'}`}>
                  <div className="msg-content">{msg.content}</div>
                  <div className="msg-time">{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <form className="chat-input-area" onSubmit={handleSend}>
        <input 
          type="text" 
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={language === 'vi' ? 'Nhập tin nhắn...' : 'Type a message...'}
        />
        <button type="submit" disabled={!newMessage.trim()}>Send</button>
      </form>
    </div>
  );
};
